import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function TerminosPage() {
  const lastUpdated = '1 de abril de 2026';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <div className="bg-gray-900 text-white py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3">Términos y Condiciones</h1>
            <p className="text-gray-400 text-sm">Última actualización: {lastUpdated}</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="prose prose-gray max-w-none space-y-10 text-gray-700 text-sm leading-relaxed">

            {/* Intro */}
            <section>
              <p className="text-base text-gray-800">
                Bienvenido a <strong>IslaVende</strong>. Al acceder o utilizar nuestra plataforma, usted acepta quedar vinculado por los presentes Términos y Condiciones. Si no está de acuerdo con alguno de estos términos, le pedimos que no utilice nuestros servicios.
              </p>
            </section>

            {/* 1 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">1. Naturaleza del Servicio — IslaVende como Facilitador</h2>
              <p className="mb-3">
                <strong>IslaVende es una plataforma de intermediación digital</strong> que conecta compradores y vendedores en Puerto Rico. IslaVende <strong>no es parte de ninguna transacción</strong> entre compradores y vendedores, y no actúa como vendedor, distribuidor, agente ni representante de ningún artículo publicado en la plataforma.
              </p>
              <p className="mb-3">
                IslaVende no fabrica, almacena, inspecciona ni garantiza la calidad, exactitud, seguridad, legalidad ni disponibilidad de los artículos listados. La responsabilidad por la descripción, condición y entrega de los artículos recae exclusivamente en el vendedor.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                <p className="font-semibold text-amber-800 mb-1">Aviso importante — Artículos "As-Is" (tal como están)</p>
                <p className="text-amber-700">
                  Los vehículos de motor, bienes raíces y artículos de segunda mano se venden en la condición en que se encuentran (<em>as-is</em>), sin garantía expresa ni implícita de ningún tipo. IslaVende no se hace responsable por el estado mecánico, estructural, legal o de titularidad de ningún vehículo o propiedad listada. El comprador es responsable de realizar su propia inspección y diligencia antes de completar cualquier transacción.
                </p>
              </div>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">2. Elegibilidad y Registro</h2>
              <p className="mb-3">Para utilizar IslaVende debe:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Tener al menos 18 años de edad.</li>
                <li>Ser residente o tener dirección válida en Puerto Rico.</li>
                <li>Proporcionar información veraz, completa y actualizada al momento del registro.</li>
                <li>Mantener la confidencialidad de sus credenciales de acceso.</li>
              </ul>
              <p className="mt-3">
                IslaVende se reserva el derecho de suspender o cancelar cuentas que proporcionen información falsa, que incumplan estos términos o que realicen actividades fraudulentas.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">3. Obligaciones del Vendedor</h2>
              <p className="mb-3">Al publicar artículos en IslaVende, el vendedor declara y garantiza que:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Es el legítimo propietario del artículo o tiene autorización para venderlo.</li>
                <li>La descripción, fotos y precio del artículo son verídicos y no inducen a error.</li>
                <li>El artículo no está sujeto a gravámenes, embargos, litigios ni restricciones legales que impidan su venta, salvo que se indique expresamente.</li>
                <li>Cumplirá con los plazos de envío acordados y notificará al comprador ante cualquier inconveniente.</li>
                <li>No publicará artículos prohibidos por la ley de Puerto Rico o los Estados Unidos.</li>
              </ul>
              <p className="mt-3">
                El vendedor acepta que IslaVende retendrá los fondos de la venta hasta que el comprador confirme la recepción del artículo, como parte del sistema de protección de compra.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">4. Obligaciones del Comprador</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>El comprador es responsable de leer detenidamente la descripción del artículo antes de realizar una compra.</li>
                <li>Para artículos de segunda mano, vehículos y bienes raíces, se recomienda encarecidamente realizar una inspección física antes de completar la transacción.</li>
                <li>El comprador acepta que IslaVende no garantiza la exactitud de las descripciones proporcionadas por los vendedores.</li>
                <li>Las disputas entre compradores y vendedores deben reportarse a IslaVende dentro de los 7 días calendario posteriores a la recepción del artículo.</li>
              </ul>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">5. Comisiones y Pagos</h2>
              <p className="mb-3">
                IslaVende cobra una comisión del <strong>15% sobre el precio de venta</strong> de cada transacción completada. Esta comisión se descuenta automáticamente antes de transferir los fondos al vendedor.
              </p>
              <p className="mb-3">
                Los pagos se procesan a través de <strong>Stripe</strong>, una plataforma de pagos certificada PCI-DSS. IslaVende no almacena información de tarjetas de crédito o débito.
              </p>
              <p>
                El IVU (Impuesto sobre Ventas y Uso) de Puerto Rico del <strong>11.5%</strong> se aplica a todas las transacciones y es responsabilidad del vendedor su declaración y pago ante el Departamento de Hacienda de Puerto Rico.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">6. Política de Devoluciones y Disputas</h2>
              <p className="mb-3">
                IslaVende ofrece protección al comprador en los siguientes casos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>El artículo no fue recibido dentro del plazo acordado.</li>
                <li>El artículo recibido es significativamente diferente a lo descrito.</li>
                <li>El artículo llegó dañado durante el envío.</li>
              </ul>
              <p className="mt-3">
                Para iniciar una disputa, el comprador debe contactar a <a href="mailto:ventas@islavende.com" className="text-gray-900 font-semibold underline">ventas@islavende.com</a> dentro de los 7 días posteriores a la recepción. IslaVende actuará como mediador, pero la decisión final sobre reembolsos queda a discreción de IslaVende.
              </p>
              <p className="mt-3">
                <strong>No se aceptan devoluciones</strong> por cambio de opinión, incompatibilidad no indicada en la descripción, o artículos vendidos expresamente como "as-is".
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">7. Artículos Prohibidos</h2>
              <p className="mb-3">Está estrictamente prohibido publicar en IslaVende:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Armas de fuego, municiones o accesorios ilegales.</li>
                <li>Sustancias controladas o medicamentos sin receta.</li>
                <li>Artículos robados o de procedencia ilícita.</li>
                <li>Material pornográfico o que explote menores de edad.</li>
                <li>Artículos que infrinjan derechos de propiedad intelectual.</li>
                <li>Animales vivos o partes de animales protegidos.</li>
                <li>Cualquier artículo cuya venta sea ilegal bajo las leyes de Puerto Rico o los Estados Unidos.</li>
              </ul>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">8. Limitación de Responsabilidad</h2>
              <p className="mb-3">
                En la máxima medida permitida por la ley aplicable, <strong>IslaVende no será responsable</strong> por daños directos, indirectos, incidentales, especiales, consecuentes o punitivos que resulten del uso o la imposibilidad de uso de la plataforma, incluyendo pero no limitado a:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Pérdidas económicas derivadas de transacciones entre usuarios.</li>
                <li>El estado físico, mecánico, legal o de titularidad de vehículos o propiedades.</li>
                <li>Daños causados por artículos defectuosos vendidos por terceros.</li>
                <li>Interrupciones del servicio por mantenimiento, fallas técnicas o causas de fuerza mayor.</li>
              </ul>
              <p className="mt-3">
                La responsabilidad máxima de IslaVende ante cualquier reclamación no excederá el monto de las comisiones cobradas en la transacción específica objeto de la reclamación.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">9. Ley Aplicable y Jurisdicción</h2>
              <p>
                Estos Términos y Condiciones se rigen por las leyes del Estado Libre Asociado de Puerto Rico y las leyes federales de los Estados Unidos de América aplicables. Cualquier disputa que no pueda resolverse amigablemente será sometida a la jurisdicción exclusiva de los tribunales competentes de Puerto Rico.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">10. Modificaciones</h2>
              <p>
                IslaVende se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Los cambios entrarán en vigor al momento de su publicación en la plataforma. El uso continuado de IslaVende después de la publicación de cambios constituye la aceptación de los nuevos términos.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3 border-b pb-2">11. Contacto</h2>
              <p>
                Para preguntas sobre estos Términos y Condiciones, puede contactarnos en:
              </p>
              <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-gray-900">IslaVende</p>
                <p className="text-gray-600">Puerto Rico</p>
                <p className="text-gray-600">
                  Email: <a href="mailto:ventas@islavende.com" className="text-gray-900 font-semibold underline">ventas@islavende.com</a>
                </p>
              </div>
            </section>

            {/* Nav links */}
            <div className="border-t pt-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
              <Link to="/privacidad" className="text-gray-900 font-semibold underline text-sm">
                Ver Política de Privacidad →
              </Link>
              <Link to="/" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                ← Volver al inicio
              </Link>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
